import axios from "axios";
import { USER_STORAGE, CSRF_TOKEN_STORAGE } from "../constants";
import {
  getIntegrityHeaders,
  shouldSignRequest,
} from "../utils/integrityHelper";
import { createRequestId, reportClientError } from "../utils/clientLogger";

const HTTP_ERROR_MESSAGES = {
  403: "Accès refusé : vous n'avez pas les droits pour effectuer cette action.",
  404: "La ressource demandée est introuvable.",
  422: "Les données envoyées sont invalides.",
  429: "Trop de requêtes. Veuillez patienter quelques instants.",
  500: "Une erreur serveur est survenue. Veuillez réessayer.",
  502: "Le serveur est temporairement indisponible.",
  503: "Service indisponible. Veuillez réessayer plus tard.",
};

const DEFAULT_API_TIMEOUT_MS = 30000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: DEFAULT_API_TIMEOUT_MS,
});

const isTransientNetworkError = (error) => {
  if (!error) return false;
  if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") return true;
  if (!error.response) return true;
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
};

let isRefreshing = false;
let failedQueue = [];
let isFetchingCsrf = false;
let csrfQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const processCsrfQueue = (error, token = null) => {
  csrfQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  csrfQueue = [];
};

const fetchCsrfToken = async () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem(USER_STORAGE));
    if (!userData?.token) return null;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}auth/csrf-token`,
      { headers: { "x-access-token": userData.token } },
    );
    const csrfToken = response.data.csrfToken;
    sessionStorage.setItem(CSRF_TOKEN_STORAGE, csrfToken);
    return csrfToken;
  } catch (error) {
    console.error("Erreur lors de la récupération du token CSRF:", error);
    return null;
  }
};

const getCsrfToken = async () => {
  let csrfToken = sessionStorage.getItem(CSRF_TOKEN_STORAGE);
  if (!csrfToken) {
    if (isFetchingCsrf) {
      return new Promise((resolve, reject) => {
        csrfQueue.push({ resolve, reject });
      });
    }
    isFetchingCsrf = true;
    try {
      csrfToken = await fetchCsrfToken();
      processCsrfQueue(null, csrfToken);
    } catch (error) {
      processCsrfQueue(error, null);
    } finally {
      isFetchingCsrf = false;
    }
  }
  return csrfToken;
};

const UNSAFE_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

api.interceptors.request.use(async (config) => {
  try {
    let userData = JSON.parse(sessionStorage.getItem(USER_STORAGE));
    config.headers["x-access-token"] = userData?.token;
    config.headers["x-request-id"] =
      config.headers["x-request-id"] || createRequestId();
    config.metadata = { ...config.metadata, startedAt: Date.now() };

    if (UNSAFE_METHODS.includes(config.method?.toUpperCase())) {
      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        config.headers["x-csrf-token"] = csrfToken;
      }

      if (shouldSignRequest(config.method, config.url, config.data)) {
        const integrityHeaders = await getIntegrityHeaders(config.data);
        Object.assign(config.headers, integrityHeaders);
      }
    }
  } catch (error) {
    reportClientError(error, { source: "axios_request_interceptor" });
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Ne pas afficher de toast pour les requêtes annulées
    if (error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // Ne pas essayer de rafraîchir le token pour les routes d'authentification
    const isAuthRoute =
      originalRequest.url?.includes("auth/login") ||
      originalRequest.url?.includes("auth/refresh-token") ||
      originalRequest.url?.includes("auth/verify-impersonation");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["x-access-token"] = token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const userData = JSON.parse(sessionStorage.getItem(USER_STORAGE));
        const refreshTokenValue = userData?.refreshToken;

        if (!refreshTokenValue) {
          throw new Error("No refresh token");
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}auth/refresh-token`,
          { refreshToken: refreshTokenValue },
        );

        const newToken = response.data.token;
        userData.token = newToken;
        sessionStorage.setItem(USER_STORAGE, JSON.stringify(userData));

        processQueue(null, newToken);
        originalRequest.headers["x-access-token"] = newToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        sessionStorage.removeItem(USER_STORAGE);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const status = error.response?.status;
    if (status && status !== 401 && !isAuthRoute) {
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.raison;
      const fallback = HTTP_ERROR_MESSAGES[status];
      if (serverMessage || fallback) {
        console.warn(`[API Error ${status}]`, serverMessage || fallback);
      }
    }

    if (!error.response && !isAuthRoute) {
      console.warn(
        "[API Network Error] Impossible de contacter le serveur. Vérifiez votre connexion.",
      );
    }

    const method = (originalRequest?.method || "get").toLowerCase();
    if (
      method === "get" &&
      isTransientNetworkError(error) &&
      !originalRequest._networkRetry
    ) {
      originalRequest._networkRetry = true;
      await new Promise((r) => setTimeout(r, 1500));
      return api(originalRequest);
    }

    if (!isAuthRoute) {
      reportClientError(error, { source: "axios_response" });
    }

    return Promise.reject(error);
  },
);

export const uploadApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: DEFAULT_UPLOAD_TIMEOUT_MS,
});

uploadApi.interceptors.request.use(async (config) => {
  try {
    let userData = JSON.parse(sessionStorage.getItem(USER_STORAGE));
    config.headers["x-access-token"] = userData?.token;
    config.headers["Content-Type"] = "multipart/form-data";
    config.headers["x-request-id"] =
      config.headers["x-request-id"] || createRequestId();
    config.metadata = { ...config.metadata, startedAt: Date.now() };

    const csrfToken = await getCsrfToken();
    if (csrfToken) {
      config.headers["x-csrf-token"] = csrfToken;
    }
  } catch (error) {
    reportClientError(error, { source: "upload_request_interceptor" });
  }
  return config;
});

uploadApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Ne pas afficher de toast pour les requêtes annulées
    if (error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const userData = JSON.parse(sessionStorage.getItem(USER_STORAGE));
        const refreshTokenValue = userData?.refreshToken;

        if (!refreshTokenValue) {
          throw new Error("No refresh token");
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}auth/refresh-token`,
          { refreshToken: refreshTokenValue },
        );

        const newToken = response.data.token;
        userData.token = newToken;
        sessionStorage.setItem(USER_STORAGE, JSON.stringify(userData));

        originalRequest.headers["x-access-token"] = newToken;
        return uploadApi(originalRequest);
      } catch (refreshError) {
        sessionStorage.removeItem(USER_STORAGE);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    const status = error.response?.status;
    if (status && status !== 401) {
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.raison;
      const fallback = HTTP_ERROR_MESSAGES[status];
      if (serverMessage || fallback) {
        console.warn(`[Upload API Error ${status}]`, serverMessage || fallback);
      }
    }

    reportClientError(error, { source: "upload_response" });

    return Promise.reject(error);
  },
);
